import { AddressBook, FunnelSimple } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { EmptyState } from '../../components/ui/Bits';
import { SearchField } from '../../components/ui/Field';
import { Popover } from '../../components/ui/Popover';
import { t } from '../../i18n';
import {
  filtreActif,
  filtrer,
  FiltresDeLAnnuaire,
  ListeParLettre,
  SANS_FILTRE,
  SANS_FORMATION,
  useAnnuaire,
  useToast,
} from './commun';

/**
 * Tous les membres d'une formation, ouverts depuis son bloc.
 *
 * La meme lecture que l'accueil de l'annuaire, sous la meme cle : l'ecran
 * s'affiche des le toucher, et la relecture se fait derriere.
 */
export function AnnuaireFormationScreen() {
  const { formation = '' } = useParams<{ formation: string }>();
  const { data, loading, error, reload, refresh } = useAnnuaire();
  const [recherche, setRecherche] = useState('');
  const [toast, flash] = useToast();
  const [filtres, setFiltres] = useState(SANS_FILTRE);
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const boutonFiltres = useRef<HTMLButtonElement>(null);

  const nom =
    formation === SANS_FORMATION
      ? t.messages.annuaire.sansFormation
      : data?.formations.find((connue) => connue.id === formation)?.nom;
  const membres = (data?.membres ?? []).filter(
    (membre) => (membre.formation ?? SANS_FORMATION) === formation,
  );
  const trouves = filtrer(membres, recherche, filtres);

  return (
    <SubScreen
      title={nom ?? t.composants.onglets.annuaire}
      onRefresh={refresh}
      roue
      overlay={
        <>
          {toast && <p className="feed__toast">{toast}</p>}
          <Popover
            open={filtresOuverts}
            anchor={boutonFiltres}
            onClose={() => setFiltresOuverts(false)}
            label={t.messages.annuaire.filtres.titre}
          >
            {/* Pas de formation a choisir : on est deja dans une. */}
            <FiltresDeLAnnuaire
              valeur={filtres}
              onChange={setFiltres}
              membres={membres}
              onFermer={() => setFiltresOuverts(false)}
            />
          </Popover>
        </>
      }
    >
      {loading && !data && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && membres.length === 0 && (
        <EmptyState icon={<AddressBook size={38} />} title={t.messages.annuaire.videFormation} />
      )}

      {data && membres.length > 0 && (
        <>
          <div className="ev-search-row an__recherche">
            <SearchField
              tone="spot"
              className="ev-search-row__field search--croix-du-fil"
              placeholder={t.messages.annuaire.rechercher}
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
              onClear={() => setRecherche('')}
            />
            <button
              ref={boutonFiltres}
              type="button"
              className="ev-filter-btn"
              aria-label={
                filtreActif(filtres)
                  ? t.messages.annuaire.filtres.actifs
                  : t.messages.annuaire.filtres.filtrer
              }
              onClick={() => setFiltresOuverts(true)}
            >
              <FunnelSimple size={20} />
              {filtreActif(filtres) && <span className="ev-filter-btn__dot" aria-hidden="true" />}
            </button>
          </div>
          {trouves.length === 0 && (
            <p className="ms-notice ms-notice--recherche">
              {recherche.trim()
                ? t.messages.annuaire.aucun(recherche.trim())
                : t.messages.annuaire.filtres.aucun}
            </p>
          )}
          <ListeParLettre membres={trouves} onEchec={flash} />
        </>
      )}
    </SubScreen>
  );
}
